import type {TenantCurrentUserInterface} from '@interfaces/tenants'
import type {UserInterface, UserProfileInterface, TenantsInterface} from '@interfaces/user'

const toNumberId = (id: number | string): number =>
  typeof id === 'number' ? id : Number.parseInt(id, 10)

const toLegacyHomeViewMode = (
  value: TenantCurrentUserInterface['home_view_mode'],
): UserProfileInterface['home_view_mode'] => (value === 'old' ? 'legacy' : value)

const toTenants = (
  tenants: TenantCurrentUserInterface['tenants'] | TenantCurrentUserInterface['organizations'],
  fallback: TenantsInterface[] = [],
): TenantsInterface[] => {
  if (!Array.isArray(tenants)) {
    return fallback
  }
  return tenants
    .filter((tenant): tenant is Exclude<typeof tenant, string> => typeof tenant !== 'string')
    .map((tenant) => ({
      id: tenant.id,
      name: tenant.name,
      domain: tenant.domain,
    }))
}

export const mergeTenantUserIntoCurrentUser = (
  currentUser: UserInterface | null,
  tenantUser: TenantCurrentUserInterface,
): UserInterface => {
  const existingProfile = currentUser?.userprofile || ({} as Partial<UserProfileInterface>)
  // The tenants service renamed current_tenant -> current_organization. Keep the
  // UI's internal `current_tenant` field name (read app-wide), sourced from the
  // backend's current_organization, falling back to the legacy field then the
  // existing value.
  const currentTenant =
    typeof tenantUser.current_organization === 'string'
      ? tenantUser.current_organization
      : typeof tenantUser.current_tenant === 'string'
        ? tenantUser.current_tenant
        : existingProfile.current_tenant || ''

  return {
    ...(currentUser || {
      tenants: [],
    }),
    tenants: toTenants(tenantUser.organizations ?? tenantUser.tenants, currentUser?.tenants || []),
    id: currentUser?.id || toNumberId(tenantUser.id),
    email: tenantUser.email,
    first_name: tenantUser.first_name,
    last_name: tenantUser.last_name,
    is_active: tenantUser.is_active ?? currentUser?.is_active,
    userprofile: {
      ...existingProfile,
      id: existingProfile.id || toNumberId(tenantUser.id),
      title: tenantUser.title ?? existingProfile.title ?? '',
      company: tenantUser.company ?? existingProfile.company ?? '',
      roles: tenantUser.permissions ?? existingProfile.roles ?? [],
      // The bypass-resolved codename set navigation visibility is derived from.
      // Left undefined when the backend omits it (older deployment) so the
      // navigation gate can fail open; an explicit empty array is preserved so a
      // user with genuinely no permissions gets the reduced menu.
      effective_permissions:
        tenantUser.effective_permissions ?? existingProfile.effective_permissions,
      is_subscription_admin:
        tenantUser.is_subscription_admin ?? existingProfile.is_subscription_admin ?? false,
      is_account_admin: tenantUser.is_account_admin ?? existingProfile.is_account_admin ?? false,
      is_line_admin: tenantUser.is_line_admin ?? existingProfile.is_line_admin ?? false,
      // Per-category Account Management capabilities. Left undefined when absent
      // (older payloads) so useCurrentUser can fall back to is_account_admin; a
      // returned false must be preserved so the sub-tab is correctly hidden.
      can_manage_users: tenantUser.can_manage_users ?? existingProfile.can_manage_users,
      can_manage_roles: tenantUser.can_manage_roles ?? existingProfile.can_manage_roles,
      can_manage_teams: tenantUser.can_manage_teams ?? existingProfile.can_manage_teams,
      can_manage_resource_groups:
        tenantUser.can_manage_resource_groups ?? existingProfile.can_manage_resource_groups,
      can_manage_notification_subscriptions:
        tenantUser.can_manage_notification_subscriptions ??
        existingProfile.can_manage_notification_subscriptions,
      // The current user's active teams ({id, name}) in the current organization,
      // used to scope the personal "my teams" notification filter. The tenants
      // payload already carries these; older payloads simply omit them.
      team_details: tenantUser.team_details ?? existingProfile.team_details,
      // A read-only platform admin (staff with no real management capability).
      // Preserve an explicit false (mirrors can_manage_* above).
      is_read_only_admin:
        tenantUser.is_read_only_admin ?? existingProfile.is_read_only_admin ?? false,
      // "View As" capability + active overlay. Preserve an explicit false/inactive
      // rather than falling back to a stale value.
      can_view_as: tenantUser.can_view_as ?? existingProfile.can_view_as ?? false,
      view_as: tenantUser.view_as ?? existingProfile.view_as ?? {active: false},
      // A waiting join request (AB#6814). An explicit null (decided, or none)
      // replaces a stale one rather than falling back to it.
      pending_join_request:
        tenantUser.pending_join_request !== undefined
          ? tenantUser.pending_join_request
          : (existingProfile.pending_join_request ?? null),
      feature_flags: tenantUser.feature_flags ?? existingProfile.feature_flags ?? [],
      // Per-service AI/Edge/AnomalyDetection read/write capability. Left undefined
      // when absent (older backend) so useServiceCapabilities falls back to
      // fully-visible; a present payload (incl. all-false) is preserved so the
      // access gates actually take effect.
      service_capabilities: tenantUser.service_capabilities ?? existingProfile.service_capabilities,
      is_demo_user: tenantUser.is_demo_user ?? existingProfile.is_demo_user ?? false,
      preferred_language:
        tenantUser.preferred_language ?? existingProfile.preferred_language ?? 'en',
      preferred_theme: tenantUser.preferred_theme ?? existingProfile.preferred_theme ?? 'dark',
      preferred_timezone: tenantUser.preferred_timezone ?? existingProfile.preferred_timezone ?? '',
      preferred_locale: tenantUser.preferred_locale ?? existingProfile.preferred_locale ?? 'en',
      home_view_mode:
        toLegacyHomeViewMode(tenantUser.home_view_mode) ?? existingProfile.home_view_mode ?? 'new',
      identity_uuid: tenantUser.identity_uuid ?? existingProfile.identity_uuid,
      has_agreed_to_terms_and_conditions:
        tenantUser.has_agreed_to_terms_and_conditions ??
        existingProfile.has_agreed_to_terms_and_conditions ??
        true,
      has_agreed_to_privacy_policy:
        tenantUser.has_agreed_to_privacy_policy ??
        existingProfile.has_agreed_to_privacy_policy ??
        true,
      onboarding_tutorial_last_step_completed:
        tenantUser.onboarding_tutorial_last_step_completed ??
        existingProfile.onboarding_tutorial_last_step_completed ??
        -2,
      onboarding_tutorial_skipped:
        tenantUser.onboarding_tutorial_skipped ??
        existingProfile.onboarding_tutorial_skipped ??
        false,
      user_type: tenantUser.user_type ?? existingProfile.user_type ?? '',
      object_id: tenantUser.object_id ?? existingProfile.object_id ?? '',
      notification_preference_anomaly_detected_machines:
        tenantUser.notification_preference_anomaly_detected_machines ??
        existingProfile.notification_preference_anomaly_detected_machines ??
        [],
      notification_preference_anomaly_detected_is_subscribed:
        tenantUser.notification_preference_anomaly_detected_is_subscribed ??
        existingProfile.notification_preference_anomaly_detected_is_subscribed ??
        false,
      notification_preference_anomaly_detected_email:
        tenantUser.notification_preference_anomaly_detected_email ??
        existingProfile.notification_preference_anomaly_detected_email ??
        false,
      notification_preference_machine_connectivity_is_subscribed:
        tenantUser.notification_preference_machine_connectivity_is_subscribed ??
        existingProfile.notification_preference_machine_connectivity_is_subscribed ??
        false,
      notification_preference_machine_connectivity_email:
        tenantUser.notification_preference_machine_connectivity_email ??
        existingProfile.notification_preference_machine_connectivity_email ??
        false,
      notification_preference_new_user_added_is_subscribed:
        tenantUser.notification_preference_new_user_added_is_subscribed ??
        existingProfile.notification_preference_new_user_added_is_subscribed ??
        false,
      notification_preference_oee_report_is_subscribed:
        tenantUser.notification_preference_oee_report_is_subscribed ??
        existingProfile.notification_preference_oee_report_is_subscribed ??
        false,
      notification_preference_oee_report_frequency:
        tenantUser.notification_preference_oee_report_frequency ??
        existingProfile.notification_preference_oee_report_frequency ??
        'daily',
      notification_preference_oee_report_machines:
        tenantUser.notification_preference_oee_report_machines ??
        existingProfile.notification_preference_oee_report_machines ??
        [],
      notification_preference_oee_report_email:
        tenantUser.notification_preference_oee_report_email ??
        existingProfile.notification_preference_oee_report_email ??
        false,
      lims_url_template: tenantUser.lims_url_template ?? existingProfile.lims_url_template ?? '',
      intercom_hmac: tenantUser.intercom_hmac ?? existingProfile.intercom_hmac,
      current_tenant: currentTenant,
    } as UserProfileInterface,
  }
}
